import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "../../misc/Dialog";
import { Button } from "../../misc/Button";
import CustomToast from "../../misc/CustomToast";
import Spinner from "../../misc/Spinner";
import { getAuthHeader } from "../../../utils/authHeader";
import {
    ZapcapRecaptionResponse,
    ZapcapTranscriptResponse,
} from "../../../types/zapcapTranscript";
import {
    CaptionToken,
    canSubmit,
    captionEditorReducer,
    hasChanges,
    toSubmitWords,
    tokensFromWords,
} from "./zapcapCaptionEditor";
import CaptionTokenEditor from "./CaptionTokenEditor";

interface EditZapcapCaptionsDialogProps {
    isOpen: boolean;
    onClose: () => void;
    postId: string;
    // Bumped by the parent when the transcript-ready socket event arrives so
    // an open dialog refetches and flips from "preparing" to editable.
    refreshToken: number;
    // Called with the recaption task id once the api accepted the request.
    // legacy=true means the prepare lane started (transcription only, no
    // render yet) — the parent subscribes but must NOT flip the post.
    onRecaptionStarted: (taskId: string, legacy: boolean) => void;
}

// MB-1460: edit the word-level captions on a Caption Video (ZapCap) post and
// send them back for a BYOT re-render. Updates EVERY unpublished post that
// shares the video (update-all — the same video is wrong everywhere it
// appears). Modeled on MB-1314's EditSlideshowCaptionsDialog; the word-token
// editor is this feature's own (zapcapCaptionEditor model).
const EditZapcapCaptionsDialog: React.FC<EditZapcapCaptionsDialogProps> = ({
    isOpen,
    onClose,
    postId,
    refreshToken,
    onRecaptionStarted,
}) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [context, setContext] = useState<ZapcapTranscriptResponse | null>(
        null
    );
    const [tokens, setTokens] = useState<CaptionToken[]>([]);
    const [updateText, setUpdateText] = useState<boolean>(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editingValue, setEditingValue] = useState<string>("");
    // Set after "Prepare transcript" so the dialog shows the waiting state
    // until the transcript-ready event triggers a refetch.
    const [preparing, setPreparing] = useState<boolean>(false);

    const loadContext = useCallback(async (): Promise<void> => {
        setLoading(true);
        try {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const { data } = await axios.get<ZapcapTranscriptResponse>(
                `${backendUrl}/post/${postId}/zapcap-transcript`,
                { headers }
            );
            setContext(data);
            setTokens(data.words ? tokensFromWords(data.words) : []);
            setEditingId(null);
            setUpdateText(false);
            // Waiting on a legacy transcription the user already started?
            setPreparing(
                !!data.pending &&
                    data.pending.legacy &&
                    !data.pending.awaitingTranscript &&
                    !data.words
            );
        } catch (error) {
            const message =
                axios.isAxiosError(error) &&
                typeof error.response?.data?.message === "string"
                    ? error.response.data.message
                    : "Couldn't load this video's captions. Please try again.";
            toast.error(<CustomToast message={message} type="error" />, {
                autoClose: 4000,
            });
            onClose();
        } finally {
            setLoading(false);
        }
    }, [postId, onClose]);

    // hasChanges is read inside the load effect, which must NOT re-run when the
    // tokens change — a ref keeps it current without widening the deps.
    const unsavedRef = useRef<boolean>(false);
    useEffect(() => {
        unsavedRef.current = hasChanges(tokens);
    }, [tokens]);

    const lastLoadRef = useRef<{ postId: string; refreshToken: number } | null>(
        null
    );
    useEffect(() => {
        if (!isOpen) {
            lastLoadRef.current = null;
            return;
        }
        const prev = lastLoadRef.current;
        lastLoadRef.current = { postId, refreshToken };
        // A refreshToken bump means "the legacy transcript is ready" — useful
        // while preparing, but a duplicate or late one must never silently
        // discard corrections the user has already typed.
        if (
            prev &&
            prev.postId === postId &&
            prev.refreshToken !== refreshToken &&
            unsavedRef.current
        ) {
            return;
        }
        void loadContext();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, postId, refreshToken]);

    const commitEdit = (): void => {
        if (!editingId) return;
        const trimmed = editingValue.trim();
        setTokens((prev) =>
            captionEditorReducer(
                prev,
                trimmed
                    ? { type: "edit", id: editingId, text: trimmed }
                    : { type: "delete", id: editingId }
            )
        );
        setEditingId(null);
    };

    const handleTokenClick = (token: CaptionToken): void => {
        if (token.deleted) {
            setTokens((prev) =>
                captionEditorReducer(prev, { type: "restore", id: token.id })
            );
            return;
        }
        setEditingId(token.id);
        setEditingValue(token.text);
    };

    const handleDeleteEditing = (): void => {
        if (!editingId) return;
        setTokens((prev) =>
            captionEditorReducer(prev, { type: "delete", id: editingId })
        );
        setEditingId(null);
    };

    const postRecaption = async (
        body: Record<string, unknown>
    ): Promise<ZapcapRecaptionResponse> => {
        const headers = await getAuthHeader();
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const { data } = await axios.post<ZapcapRecaptionResponse>(
            `${backendUrl}/post/${postId}/zapcap-recaption`,
            body,
            { headers }
        );
        return data;
    };

    const handleSave = async (): Promise<void> => {
        if (!context || !canSubmit(tokens)) return;
        setSaving(true);
        try {
            const data = await postRecaption({
                words: toSubmitWords(tokens),
                updateText,
            });
            onRecaptionStarted(data.taskId, false);
            onClose();
            toast.success(
                <CustomToast
                    message="Your video is being re-captioned. Every unpublished post that uses it will be updated."
                    type="success"
                />,
                { autoClose: 5000 }
            );
        } catch (error) {
            // Leave the dialog open with the user's edits intact.
            const message =
                axios.isAxiosError(error) &&
                typeof error.response?.data?.message === "string"
                    ? error.response.data.message
                    : "Couldn't start the caption update. Please try again.";
            toast.error(<CustomToast message={message} type="error" />, {
                autoClose: 5000,
            });
        } finally {
            setSaving(false);
        }
    };

    // Legacy lane: this post predates word-level transcript storage — ask
    // ZapCap to re-transcribe, then the editor opens on the fresh words.
    const handlePrepareTranscript = async (): Promise<void> => {
        setSaving(true);
        try {
            const data = await postRecaption({ updateText: false });
            setPreparing(true);
            onRecaptionStarted(data.taskId, true);
            toast.success(
                <CustomToast
                    message="Preparing the transcript - this usually takes a minute or two. We'll let you know when it's ready to edit."
                    type="success"
                />,
                { autoClose: 6000 }
            );
        } catch (error) {
            const message =
                axios.isAxiosError(error) &&
                typeof error.response?.data?.message === "string"
                    ? error.response.data.message
                    : "Couldn't prepare the transcript. Please try again.";
            toast.error(<CustomToast message={message} type="error" />, {
                autoClose: 5000,
            });
        } finally {
            setSaving(false);
        }
    };

    const renderInFlight =
        !!context?.pending && !context.pending.awaitingTranscript && !preparing;
    const editable = !!context && !context.legacy && tokens.length > 0;
    const isBusy = loading || saving;
    // No spoken words were detected when the video was captioned: there is
    // nothing to edit, and preparing a transcript would find nothing again.
    const noSpeech = !!context?.noSpeech;

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="relative flex max-h-[calc(90dvh-2rem)] flex-col overflow-y-hidden p-0 bg-white min-[768px]:max-w-3xl">
                <div className="relative shrink-0 border-b border-gray-200 bg-white px-4 py-3 min-[640px]:px-6 min-[768px]:px-8">
                    <button
                        className="absolute right-4 top-4 text-secondary hover:text-primary focus:outline-none"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <FaTimes className="h-6 w-6" />
                    </button>
                    <DialogHeader className="pr-8">
                        <DialogTitle className="text-lg min-[640px]:text-xl min-[768px]:text-2xl">
                            Edit Captions
                        </DialogTitle>
                        <DialogDescription className="text-sm min-[640px]:text-base">
                            {noSpeech
                                ? "There are no captions to edit on this video."
                                : "Click a word to correct it. Corrections re-render the video and update every unpublished post that uses it."}
                        </DialogDescription>
                    </DialogHeader>
                </div>

                <div className="flex min-h-0 flex-1 flex-col space-y-4 overflow-y-auto px-4 pb-4 pt-4 min-[640px]:px-6 min-[768px]:px-8">
                    {loading ? (
                        <div className="flex flex-1 flex-col items-center justify-center gap-4 py-12">
                            <Spinner />
                            <p className="text-sm text-gray-500">
                                Loading the transcript…
                            </p>
                        </div>
                    ) : noSpeech ? (
                        <div className="flex flex-col gap-3 py-2">
                            <p className="text-base font-semibold text-primary">
                                No captions for this video
                            </p>
                            <p className="text-sm text-gray-700">
                                We ran this video through our captioning
                                process, but no spoken words were detected
                                - so there&apos;s no transcript to edit
                                and no captions were added to the video. Your
                                post will publish with the original video as
                                uploaded.
                            </p>
                            <p className="text-sm text-gray-700">
                                We added a short caption to your post for you
                                - you can change it anytime from the post
                                editor.
                            </p>
                            <p className="text-sm text-gray-700">
                                If this video does include speech, a version
                                with clearer audio will caption better.
                            </p>
                        </div>
                    ) : renderInFlight ? (
                        <div className="flex flex-1 flex-col items-center justify-center gap-4 py-12 text-center">
                            <Spinner />
                            <p className="text-sm font-medium text-primary">
                                A caption update is already in progress for
                                this video. You can close this window - the
                                posts update automatically when it finishes.
                            </p>
                        </div>
                    ) : preparing ? (
                        <div className="flex flex-1 flex-col items-center justify-center gap-4 py-12 text-center">
                            <Spinner />
                            <p className="text-sm font-medium text-primary">
                                Preparing the transcript… This usually takes a
                                minute or two. The editor will open here as
                                soon as it&apos;s ready.
                            </p>
                        </div>
                    ) : editable ? (
                        <>
                            <CaptionTokenEditor
                                tokens={tokens}
                                editingId={editingId}
                                editingValue={editingValue}
                                setEditingValue={setEditingValue}
                                onCommitEdit={commitEdit}
                                onCancelEdit={() => setEditingId(null)}
                                onDeleteEditing={handleDeleteEditing}
                                onTokenClick={handleTokenClick}
                            />
                            <label className="flex items-center gap-2 text-sm text-gray-700">
                                <input
                                    type="checkbox"
                                    checked={updateText}
                                    onChange={(e) =>
                                        setUpdateText(e.target.checked)
                                    }
                                    className="h-4 w-4"
                                />
                                Also update the post text from the corrected
                                transcript
                            </label>
                        </>
                    ) : (
                        context && (
                            <div className="flex flex-col gap-4">
                                <p className="text-sm text-gray-700">
                                    This video was captioned before word-level
                                    editing existed, so its transcript needs to
                                    be prepared first. Preparing re-transcribes
                                    the video and opens the editor when it&apos;s
                                    ready.
                                </p>
                                {context.text ? (
                                    <div className="max-h-48 overflow-y-auto rounded-md border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                                        {context.text}
                                    </div>
                                ) : null}
                            </div>
                        )
                    )}
                </div>

                <div className="flex shrink-0 flex-col justify-end gap-2 border-t border-gray-200 bg-white px-4 py-4 min-[480px]:flex-row min-[640px]:px-6 min-[768px]:px-8">
                    {editable && !renderInFlight ? (
                        <Button
                            id="save-zapcap-captions-btn"
                            onClick={handleSave}
                            disabled={
                                isBusy ||
                                !hasChanges(tokens) ||
                                !canSubmit(tokens)
                            }
                        >
                            {saving ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Saving…
                                </>
                            ) : (
                                "Save & Re-caption Video"
                            )}
                        </Button>
                    ) : null}
                    {!editable &&
                    !renderInFlight &&
                    !preparing &&
                    !noSpeech &&
                    context ? (
                        <Button
                            id="prepare-zapcap-transcript-btn"
                            onClick={handlePrepareTranscript}
                            disabled={isBusy}
                        >
                            {saving ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Starting…
                                </>
                            ) : (
                                "Prepare Transcript"
                            )}
                        </Button>
                    ) : null}
                    {noSpeech && !loading ? (
                        <Button
                            id="close-zapcap-captions-btn"
                            onClick={onClose}
                        >
                            Close
                        </Button>
                    ) : null}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default EditZapcapCaptionsDialog;
